import {v4 as uuidv4} from 'uuid'
import {exhaustive} from '@freckle/exhaustive'
import {fromMaybe, mthen} from '@freckle/maybe'
import {EchoError} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/echo-error'
import {
  ActionableHttpError,
  NonActionableHttpError
} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/http-error'
import {isNetworkError} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/is-network-error'
import {
  ClientMalformedRequestError,
  ClientNetworkError,
  ClientRequestAbortError
} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/native-fetch-errors'
import {ActionableUnknownError} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/unknown-error'
import {updateClientErrorStack as updateErrorStack} from '@freckle/student-entities/ts/common/helpers/ajax/helpers/errors/update-client-error-stack'

const REQUEST_ECHO = uuidv4()

const sides = (() => {
  const sides = new URL(window.location.href).searchParams.get('sides')
  if (sides !== null && sides.trim() !== '') {
    return sides
  }
  return null
})()

export const mkWrappedFetch =
  (onEchoError?: (echoError: EchoError) => void) => async (url: string, init: RequestInit) => {
    const originalStack = new Error().stack

    let urlWithXEcho: URL | undefined = undefined
    try {
      urlWithXEcho = new URL(url)
      urlWithXEcho.searchParams.delete('x-echo')
      urlWithXEcho.searchParams.append('x-echo', REQUEST_ECHO)
      if (sides !== null) {
        urlWithXEcho.searchParams.append('sides', sides)
      }
    } catch (_e) {}

    try {
      const response = await window.fetch(urlWithXEcho ?? url, init)
      const {status, headers} = response

      const responseEcho = headers.get('X-Echo')
      if (responseEcho && REQUEST_ECHO !== responseEcho && onEchoError) {
        onEchoError(
          new EchoError(
            `X-Echo expected "${REQUEST_ECHO}", but found "${responseEcho}"`,
            Array.from(headers.entries()).join('\n')
          )
        )
        return response
      }

      if (!response.ok) {
        const {method} = init

        const errorConstructor =
          [401, 403].includes(status) || status >= 500
            ? NonActionableHttpError
            : ActionableHttpError

        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          throw new errorConstructor(url, method ?? 'GET', response.clone().status, {
            response: response.clone(),
            json: await response.clone().json()
          })
        } else {
          // API returned unexpected non 2xx response code
          throw new errorConstructor(url, method ?? 'GET', response.clone().status, {
            response: response.clone(),
            text: await response.clone().text()
          })
        }
      }
      return response
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        // request aborted via AbortController's abort()
        throw updateErrorStack(new ClientRequestAbortError(e.message, e), originalStack)
      }
      if (isNetworkError(e)) {
        // Network issues preventing client to make the request
        throw updateErrorStack(new ClientNetworkError(e.message, e), originalStack)
      }
      if (e instanceof TypeError) {
        // The supplied Fetch options/url are invalid
        throw updateErrorStack(new ClientMalformedRequestError(e.message, e), originalStack)
      }
      // The error thrown inside if(!response.ok)
      if (e instanceof ActionableHttpError || e instanceof NonActionableHttpError) {
        throw updateErrorStack(e, originalStack)
      }

      if (e instanceof Error) {
        throw updateErrorStack(
          new ActionableUnknownError(`${e.name}: ${e.message}`, e),
          originalStack
        )
      }

      throw updateErrorStack(new ActionableUnknownError('Unexpected error', e), originalStack)
    }
  }

// Passing in the already built `Request` to `new Request` does not work correctly.
// It complains about not setting a `duplex` property
// When that property is manually set then Chrome complains about HTTP2 vs HTTP1 request
// This instead copies all the properties in the `Request` into a new `RequestInit` and forces the body to be a string
export async function mkRequestInit(request: Request): Promise<RequestInit> {
  const text = await request.text()
  return {
    method: request.method,
    keepalive: request.keepalive,
    headers: request.headers,
    // N.B. TypeScript thinks `request.body : ReadableStream | null` but it can also be undefined...
    body: text === '' ? undefined : text,
    redirect: request.redirect,
    integrity: request.integrity,
    signal: request.signal,
    credentials: request.credentials,
    mode: request.mode,
    referrer: request.referrer,
    referrerPolicy: request.referrerPolicy
  }
}

type MethodT = 'POST' | 'GET' | 'PATCH' | 'PUT' | 'HEAD' | 'DELETE'
type ContentTypeT =
  | 'application/json; charset=utf-8'
  | 'application/x-www-form-urlencoded'
  | 'text/plain'
  | 'text/csv'
type DataTypeT = 'json' | 'text'

type AjaxCallOptionsT = {
  url: string
  method: MethodT
  data?: string | null
  query?: Record<string, string | number | boolean> | null
  contentType?: ContentTypeT | null
  dataType: DataTypeT
  cache?: boolean
  xhrFields?: {withCredentials: boolean}
  timeout?: number
}

const wrappedFetch = mkWrappedFetch()

export function ajaxCall<T>(options: AjaxCallOptionsT): Promise<T> {
  const {url, method, data, query, contentType, dataType, cache, xhrFields, timeout} = options
  const contentTypeHeader: Record<'contentType', ContentTypeT> | Record<never, never> =
    contentType !== null && contentType !== undefined ? {'Content-Type': contentType} : {}

  const controller = new AbortController()

  // timeout 0 means no timeout
  if (timeout !== undefined && timeout !== null && timeout !== 0) {
    setTimeout(() => controller.abort(), timeout)
  }

  const inputUrl = new URL(url)

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      inputUrl.searchParams.append(key, value.toString())
    }
  }

  return wrappedFetch(inputUrl.toString(), {
    method,
    cache: cache ? 'default' : 'no-store',
    credentials: xhrFields?.withCredentials === false ? 'omit' : 'include',
    headers: new Headers({...contentTypeHeader}),
    body: data ?? null,
    signal: controller.signal
  }).then(res => {
    if (res.ok) {
      switch (dataType) {
        case 'json':
          return res.json()
        case 'text':
          return res.text()
        default:
          return exhaustive(dataType)
      }
    }
  })
}

export type AjaxJsonCallOptionsT = Pick<
  AjaxCallOptionsT,
  'url' | 'method' | 'data' | 'query' | 'cache' | 'xhrFields' | 'timeout'
>

export function ajaxJsonCall<T>(options: AjaxJsonCallOptionsT): Promise<T> {
  const {url, method, data, query, cache, xhrFields, timeout} = options
  // If we are not sending any data along with the request then there is no need to specify the contentType
  // For cross-domain requests, setting the content type to anything other than application/x-www-form-urlencoded,
  // multipart/form-data, or text/plain will trigger the browser to send a preflight OPTIONS request to the server.
  // We don't want to make a preflight request where it has no use.
  const contentType = data !== null && data !== undefined ? 'application/json; charset=utf-8' : null
  const dataType = 'json'
  return ajaxCall({url, method, data, query, contentType, dataType, cache, xhrFields, timeout})
}

type AjaxFormCallOptionsT = Pick<AjaxCallOptionsT, 'url' | 'method' | 'data'>

export function ajaxFormCall<T>(options: AjaxFormCallOptionsT): Promise<T> {
  const {url, method, data} = options
  const contentType = 'application/x-www-form-urlencoded'
  const dataType = 'json'
  const cache = false
  return ajaxCall({url, method, data, contentType, dataType, cache})
}

type AjaxFormFileUploadOptionsT = Pick<AjaxCallOptionsT, 'url' | 'method' | 'data' | 'timeout'>

export function ajaxFormFileUpload<T>(options: AjaxFormFileUploadOptionsT): Promise<T> {
  const {url, data, method, timeout} = options
  return ajaxCall({
    url,
    method: method ? method : 'POST',
    data,
    timeout,
    dataType: 'text'
  })
}

export type AjaxFileDownloadOptionsT = {
  url: string
  accept: ContentTypeT
  defaultFilename: string
}

export function ajaxFileDownload(options: AjaxFileDownloadOptionsT): Promise<void> {
  const {url, accept, defaultFilename} = options
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('GET', url, true)
    request.withCredentials = true
    request.responseType = 'blob'
    request.setRequestHeader('Accept', accept)

    // Reject on error
    request.onerror = () => {
      reject({
        status: request.status,
        statusText: request.statusText
      })
    }

    // Create an anchor that downloads Blob using FileReader
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        const contentType = request.getResponseHeader('Content-Type')
        const disposition = request.getResponseHeader('Content-Disposition')
        const blob = new Blob([request.response ?? ''], {type: contentType ?? undefined})
        const reader = new FileReader()
        reader.onload = e => {
          const anchor = document.createElement('a')
          anchor.style.display = 'none'

          const target = e.target
          if (target instanceof FileReader && typeof target.result === 'string') {
            anchor.href = target.result
            anchor.download = fromMaybe(
              () => defaultFilename,
              contentDispositionFilename(disposition)
            )
            anchor.click()
            resolve()
          } else {
            reject({
              status: request.status,
              statusText: request.statusText
            })
          }
        }
        reader.readAsDataURL(blob)
      } else {
        reject({
          status: request.status,
          statusText: request.statusText
        })
      }
    }

    // Go
    request.send()
  })
}

function contentDispositionFilename(mDisposition?: string | null): string | undefined | null {
  return mthen(mDisposition, disposition =>
    mthen(
      disposition.trim().match(/attachment; filename="(.*)"/),
      ([_ignore, filename]) => filename
    )
  )
}

type SendBeaconOptionsT = Inexact<{
  url: string
  data: Inexact<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [x: string]: any
  }> // Object to stringify
}>

export function sendBeacon(options: SendBeaconOptionsT) {
  const {url, data} = options

  try {
    const jsonData = JSON.stringify(data)
    window.navigator.sendBeacon(url, jsonData)
  } catch (_e) {}
}

export function checkUrlExistence(url: string): Promise<boolean> {
  return new Promise(resolve => {
    ajaxCall({
      url,
      method: 'HEAD',
      contentType: 'text/plain',
      dataType: 'text'
    })
      .then(() => {
        resolve(true)
      })
      .catch(() => {
        resolve(false)
      })
  })
}

/**
 * This hack gets around a Chrome caching bug wherein the audio request generated
 * by the audio web api leads to chrome caching a response that does not contain
 * the appropriate CORS headers.  Any subsequent testing of that resource by this method
 * would then attempt to fetch the resource from cache and would error out with a missing
 * access-control-allow-origin error.  By appending the path name to the audioPath
 * here, but not for the audio web api, we create a separate cache for this
 * resource request and bypass using the cached response with the missing
 * CORS Headers.  This is reproducible in Chrome only.
 *
 * Root cause: https://bugs.chromium.org/p/chromium/issues/detail?id=260239
 */
export function appendParamToRemedyCorsBug(path: string): string {
  return path.includes('?') ? `${path}&via=xmlHttpRequest` : `${path}?via=xmlHttpRequest`
}
